import React, { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FilterListIcon from '@mui/icons-material/FilterList'
import axios from 'axios'
import toast from 'react-hot-toast'
import AppModal from '../../../components/AppModal'
import AppTable from '../../../shared/AppTable'
import AppButton from '../../../shared/AppButton'
import CustomFilterInput from '../../../shared/CustomFilterInput'
import TableSkeletonLoader from '../../../common/TableLoading'
import Pagination from '../../../components/Pagination'
import claimsGQL from '../../../gql/claims'
import { API_BASE_URL } from '../../../config/apiConfig'

export default function InvalidICDCodeModal({
  open,
  onClose,
  initialFromDate = '',
  initialToDate = '',
  initialErrorCode = 'IB-00242'
}) {
  const { t } = useTranslation()
  const [fromDate, setFromDate] = useState(initialFromDate)
  const [toDate, setToDate] = useState(initialToDate)
  const [errorCode, setErrorCode] = useState(initialErrorCode)
  const [exporting, setExporting] = useState(false)
  const [page, setPage] = useState(1)

  const [fetchIssueCodes, { loading, data }] = useLazyQuery(
    claimsGQL.GET_CLAIM_DIAGNOSIS_ISSUE_CODES,
    {
      fetchPolicy: 'network-only'
    }
  )

  useEffect(() => {
    if (open) {
      setFromDate(initialFromDate)
      setToDate(initialToDate)
      setErrorCode(initialErrorCode || 'IB-00242')
      setPage(1)
      loadCodes(initialFromDate, initialToDate, initialErrorCode || 'IB-00242', 1)
    }
  }, [open, initialFromDate, initialToDate, initialErrorCode])

  const loadCodes = (fDate, tDate, errCode, currentPage = 1) => {
    fetchIssueCodes({
      variables: {
        input: {
          from_date: fDate,
          to_date: tDate,
          error_value: errCode,
          limit: 50,
          page: currentPage
        }
      }
    })
  }

  const handleApplyFilter = () => {
    setPage(1)
    loadCodes(fromDate, toDate, errorCode, 1)
  }

  const handlePageChange = newPage => {
    setPage(newPage)
    loadCodes(fromDate, toDate, errorCode, newPage)
  }

  const handleExportExcel = async () => {
    try {
      setExporting(true)
      const token = localStorage.getItem('token')?.replaceAll('"', '')
      const response = await axios.get(
        `${API_BASE_URL}/claim/diagnosis/bulk-replace/issues/export`,
        {
          params: {
            from_date: fromDate,
            to_date: toDate,
            error_code: errorCode
          },
          responseType: 'blob',
          headers: {
            ...(token && { Authorization: `Bearer ${token}` })
          }
        }
      )
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `invalid_icd_codes_${fromDate || 'all'}_to_${toDate || 'all'}.xlsx`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success(t('Excel file downloaded successfully'))
    } catch (err) {
      console.error(err)
      toast.error(t('Failed to export Excel file'))
    } finally {
      setExporting(false)
    }
  }

  const responseData = data?.getClaimDiagnosisIssueCodes
  const groupedIssueData = responseData?.groups || []
  const resultData = responseData?.data || []
  const hasGroupedData = groupedIssueData?.length > 0
  const totalRecords = data?.getClaimDiagnosisIssueCodes?.total || 0
  const totalPages = Math.ceil(totalRecords / 50) || 1

  const columns = [
    {
      header: t('SQ #'),
      align: 'center',
      render: (row, index) => (
        <span className='font-semibold text-slate-600 text-xs'>
          {(page - 1) * 50 + index + 1}
        </span>
      )
    },
    {
      header: t('Issue Explanation'),
      render: row => (
        <span
          className='block max-w-[360px] truncate text-xs font-medium text-slate-600'
          title={row?.issue_description || ''}
        >
          {row?.issue_description || '---'}
        </span>
      )
    },
    {
      header: t('ICD Code'),
      render: row => (
        <div className='flex items-center gap-1.5'>
          <span>{row?.icd_code_id || '---'}</span>
        </div>
      )
    },
    {
      header: t('Description'),
      render: row => (
        <span className='text-xs font-medium text-slate-700'>
          {row?.description || '---'}
        </span>
      )
    },
    {
      header: t('Occurrence Count'),
      align: 'center',
      render: row => <span>{row?.occurrence_count ?? 0}</span>
    }
  ]

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={t('Invalid ICD Codes Summary')}
      maxWidth='lg'
    >
      <div className='flex flex-col gap-3'>
        <div className='flex flex-wrap items-end gap-2 bg-slate-50/80 px-3 pt-2 pb-3 rounded-md border border-slate-200 justify-between shadow-none'>
          <div className='flex flex-wrap items-end gap-2'>
            <div className='w-36'>
              <CustomFilterInput
                label={t('From Date')}
                type='date'
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>
            <div className='w-36'>
              <CustomFilterInput
                label={t('To Date')}
                type='date'
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>
            <AppButton
              label={t('Filter')}
              onClick={handleApplyFilter}
              startIcon={<FilterListIcon style={{ fontSize: 16 }} />}
              className='!h-[28px] !text-[12px]'
            />
          </div>

          <div className='flex items-center gap-2'>
            <AppButton
              label={exporting ? t('Exporting...') : t('Download Excel')}
              variant='outlined'
              onClick={handleExportExcel}
              disabled={exporting}
              startIcon={<FileDownloadOutlinedIcon style={{ fontSize: 16 }} />}
              className='!border-emerald-600 !text-emerald-700 hover:!bg-emerald-50 !h-[28px] !text-[12px]'
            />
          </div>
        </div>

        {loading ? (
          <div className='min-h-fit bg-white rounded-md border border-slate-200 overflow-hidden'>
            <TableSkeletonLoader columnCount={columns.length} rowCount={5} />
          </div>
        ) : hasGroupedData ? (
          <div className='space-y-3'>
            {groupedIssueData?.map((group, groupIndex) => (
              <div
                key={`${group?.error_code || 'issue'}__${group?.issue_description || groupIndex}`}
                className='overflow-hidden rounded-md border border-slate-200 bg-white'
              >
                <div className='flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2'>
                  <div className='min-w-0 flex-1'>
                    <div className='flex flex-wrap items-center gap-2'>
                      <span className='rounded bg-[#0070C2]/10 px-2 py-0.5 text-xs font-bold text-[#0070C2]'>
                        {group?.error_code || '---'}
                      </span>
                      <span className='text-xs font-semibold text-slate-600'>
                        {t('Occurrences')}: {group?.occurrence_count ?? 0}
                      </span>
                    </div>
                    <p className='mt-1 text-xs font-medium leading-5 text-slate-700'>
                      {group?.issue_description || t('No issue explanation found')}
                    </p>
                  </div>
                </div>
                <AppTable
                  columns={columns}
                  data={group?.codes || []}
                  loading={false}
                  actions={false}
                  bodyRowPadding='8px 12px'
                />
              </div>
            ))}
          </div>
        ) : (
          <div className='min-h-fit bg-white rounded-md border border-slate-200 overflow-hidden'>
            <AppTable
              columns={columns}
              data={resultData}
              loading={loading}
              actions={false}
              bodyRowPadding='8px 12px'
              LoaderComponent={
                <TableSkeletonLoader columnCount={columns.length} rowCount={5} />
              }
            />
          </div>
        )}

        {totalRecords > 0 && (
          <div className='mt-0.5' >
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </AppModal>
  )
}
